import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { get, patch, post } from 'utils/network';
import { maskPhone, unmaskPhone } from 'utils/transforms';
import { ApiRoute, ErrorMessage, FeatureFlag } from 'config';

import type { AddressErrors, IAddress, ZipInfo } from 'models/Address';
import Address, { State } from 'models/Address';
import useCurrentUser from 'hooks/useCurrentUser';

import { Component as CaretIcon } from 'icons/icon-ui--caret.svg';
import Checkbox from 'components/shared/Checkbox';
import ValidableControl from 'components/shared/ValidableControl';
import DeliveryInstructionsTooltip from './tooltips/DeliveryInstructionsTooltip';

type Props = {
  oauthInfo?: Record<string, string>;
  initialAddress?: Address;
  initialErrors?: AddressErrors;
  submitButtonDisabled?: boolean;
  submitButtonLabel?: ReactNode;
  onSuccess: () => void;
  updateBillingAddress?: boolean;
};

enum ComponentLengthType {
  STREET_NUMBER = 'short_name',
  ROUTE = 'long_name',
}

const MAX_ADDRESS2_LENGTH = 30;

// TODO: consolidate duplicate address form components/logic (BillingAddressView, AddressModal)
function AddressView({
  oauthInfo,
  initialAddress,
  initialErrors = {} as AddressErrors,
  submitButtonDisabled = false,
  submitButtonLabel = 'Next',
  onSuccess,
  updateBillingAddress,
}: Props): ReactElement {
  const { currentUser } = useCurrentUser();

  const getAttr = (attr) => (initialAddress ? initialAddress[attr] : '');
  const oauthFirstName = oauthInfo?.first_name;
  const oauthLastName = oauthInfo?.last_name;
  const [firstName, setFirstName] = useState(getAttr('first_name'));
  const [lastName, setLastName] = useState(getAttr('last_name'));
  const [address1, setAddress1] = useState(getAttr('address'));
  const [address2, setAddress2] = useState(getAttr('address2'));
  const [zip, setZip] = useState(getAttr('zip'));
  const [state, setState] = useState(getAttr('state'));
  const [city, setCity] = useState(getAttr('city'));
  const [phone, setPhone] = useState(maskPhone(getAttr('local_phone_number')));
  const [showAddress2, setShowAddress2] = useState(false);

  const [smsAccountAlerts, setSmsAccountAlerts] = useState(
    getAttr('sms_notification_status') === 'sms_opted_in',
  );
  const [smsOffersAlerts, setSmsOffersAlerts] = useState(
    getAttr('sms_offers_status') === 'sms_opted_in',
  );

  const [deliveryInstructions, setDeliveryInstructions] = useState(
    getAttr('delivery_instructions'),
  );

  const baseRef = useRef<HTMLDivElement | null>(null);
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const addressInputRef = useRef();
  const address2InputRef = useRef();
  const zipRef = useRef();
  const stateRef = useRef();
  const cityRef = useRef();
  const localPhoneNumberRef = useRef();
  const deliveryInstructionsRef = useRef();

  const refsArray = useRef([
    { base: baseRef },
    { first_name: firstNameRef },
    { last_name: lastNameRef },
    { address: addressInputRef },
    { address2: address2InputRef },
    { zip: zipRef },
    { state: stateRef },
    { city: cityRef },
    { local_phone_number: localPhoneNumberRef },
    { delivery_instructions: deliveryInstructionsRef },
  ]);

  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState(initialErrors);
  const [hasFedExError, setFedExError] = useState(false);
  const getErrorMsg = (attr) => (errors[attr] ? errors[attr][0] : '');
  const isInvalid = (attr) => !!errors[attr];

  const isFedExValidationEnabled = currentUser
    .hasFeatureEnabled(FeatureFlag.FEDEX_VALIDATION);

  useEffect(() => {
    if (oauthFirstName && oauthLastName) {
      setFirstName(oauthFirstName);
      setLastName(oauthLastName);
    }

    (async () => {
      if (zip.length === 5) {
        const response = await get<ZipInfo>(`/zip_codes/${zip}`);

        if (response.ok) {
          // @ts-expect-error TODO: fix null safety
          const stateCode = response.parsedBody.state_code;
          // @ts-expect-error TODO: fix null safety
          const cityName = response.parsedBody.city;

          if (stateCode && cityName) {
            setState(stateCode);
            setCity(cityName);
          }
        }
      }
    })();
  }, [zip, oauthFirstName, oauthLastName]);

  useEffect(() => {
    if (window.google) {
      const inputElement = addressInputRef.current;
      const autoComplete = new window.google.maps.places.Autocomplete(
        // @ts-expect-error TODO: fix null safety
        (inputElement),
        {
          types: ['geocode'],
          componentRestrictions: { country: 'us' },
        },
      );

      autoComplete.addListener('place_changed', () => {
        const components = autoComplete.getPlace()?.address_components;

        if (components === undefined) return;

        setAddress1([
          components[0][ComponentLengthType.STREET_NUMBER],
          components[1][ComponentLengthType.ROUTE],
        ].join(' '));

        setState('');
        setCity('');
        setZip('');

        for (let i = 0; i < components.length; i++) {
          const addressType = components[i].types[0];
          const addressLengthType = ComponentLengthType[addressType.toUpperCase()] || 'short_name';

          if (addressType !== 'street_number' && addressType !== 'route') {
            const val = components[i][addressLengthType];

            if (addressType === 'administrative_area_level_1') {
              setState(val);
            } else if (addressType === 'locality') {
              setCity(val);
            } else if (addressType === 'postal_code') {
              setZip(val);
            }
          }
        }
      });
    }
  }, []);

  const handleSubmit = async () => {
    const payload = {
      shipping_address: {
        address: address1,
        address2,
        city,
        delivery_instructions: deliveryInstructions,
        sms_notification_status: smsAccountAlerts
          ? 'sms_opted_in'
          : 'sms_opted_out',
        sms_offers_status: smsOffersAlerts
          ? 'sms_opted_in'
          : 'sms_opted_out',
        first_name: firstName,
        last_name: lastName,
        local_phone_number: unmaskPhone(phone),
        primary: true,
        state,
        zip,
        validate_address: isFedExValidationEnabled && !hasFedExError,
      } as IAddress,
      same_as_billing: updateBillingAddress,
    };

    setLoading(true);

    const response = await (
      initialAddress?.id
        ? patch(`${ApiRoute.SHIPPING_ADDRESSES}/${initialAddress.id}`, payload)
        : post(ApiRoute.SHIPPING_ADDRESSES, payload)
    );

    setLoading(false);

    if (response.status === 422) {
      const parsedBody = response.parsedBody as AddressErrors;

      if (isFedExValidationEnabled) {
        if (
          address1 && parsedBody.address
          && Object.keys(parsedBody).length === 1
        ) {
          setFedExError(true);
          parsedBody.base = [ErrorMessage.UNVERIFIED_ADDRESS];
          if (address2) parsedBody.address2 = parsedBody.address;
        } else if (address1) {
          delete parsedBody.address;
        }
      }

      setErrors(parsedBody);
    } else {
      setErrors({} as AddressErrors);
      onSuccess();
    }
  };

  useEffect(() => {
    if (address2.length <= MAX_ADDRESS2_LENGTH - 1) {
      setErrors((lastErrors) => {
        const newErrors = { ...lastErrors };
        delete newErrors.address2;
        return newErrors;
      });
    } else {
      setErrors((lastErrors) => ({
        ...lastErrors,
        address2: [ErrorMessage.CHAR_LIMIT_ADDRESS2],
      }));
    }
  }, [address2]);

  const handleAddress2Change = (value) => {
    setAddress2((lastValue) => {
      if (value.length <= MAX_ADDRESS2_LENGTH) return value;

      return value.length < lastValue.length
        ? value
        : lastValue;
    });
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0) return;

    // Looping over the refsArray to find the first one with an error
    refsArray.current.some((ref) => {
      const fieldName = Object.keys(ref)[0];
      if (errors[fieldName] && ref[fieldName].current) {
        ref[fieldName].current.scrollIntoView({ behavior: 'smooth' });
        return true;
      }
      return false;
    });
  }, [errors, refsArray]);

  return (
    <div className="row">
      {isInvalid('base') && (
        <div className="mb-4 col-12 u-text--sm">
          <div className="c-alert c-alert--error" role="alert">
            <div className="c-alert__text" ref={baseRef ?? null}>
              {getErrorMsg('base')}
            </div>
          </div>
        </div>
      )}
      <div className="col-xs-6 col-12">
        <ValidableControl
          label="First Name *"
          htmlFor="add-address-first-name"
          isInvalid={isInvalid('first_name')}
          errorMessage={getErrorMsg('first_name')}
        >
          <input
            id="add-address-first-name"
            aria-label="add address first name"
            // @ts-expect-error TODO: fix null safety
            ref={firstNameRef}
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-xs-6 col-12">
        <ValidableControl
          label="Last Name *"
          htmlFor="add-address-last-name"
          isInvalid={isInvalid('last_name')}
          errorMessage={getErrorMsg('last_name')}
        >
          <input
            id="add-address-last-name"
            aria-label="add address last name"
            // @ts-expect-error TODO: fix null safety
            ref={lastNameRef}
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-12">
        <ValidableControl
          label="Street Address *"
          htmlFor="add-address-address"
          isInvalid={isInvalid('address')}
          errorMessage={getErrorMsg('address')}
        >
          <input
            id="add-address-address"
            aria-label="add address address"
            // @ts-expect-error TODO: fix null safety
            ref={addressInputRef}
            type="text"
            value={address1}
            onChange={(e) => setAddress1(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-12">
        {showAddress2 ? (
          <ValidableControl
            label={(
              <>
                Apartment/Suite&nbsp;
                <span>(optional)</span>
              </>
            )}
            htmlFor="add-address-address2"
            isInvalid={isInvalid('address2')}
            invalidClassName={address2.length > MAX_ADDRESS2_LENGTH
              ? 'is-invalid'
              : 'has-warning'}
            errorMessage={getErrorMsg('address2')}
          >
            <input
              id="add-address-address2"
              aria-label="add address address 2"
              // @ts-expect-error TODO: fix null safety
              ref={address2InputRef}
              type="text"
              value={address2}
              onChange={(e) => handleAddress2Change(e.target.value)}
            />
          </ValidableControl>
        ) : (
          <div className="c-form-group">
            <button
              type="button"
              aria-label="Expand Apartment / Suite field"
              className="c-link u-text--sm"
              onClick={() => setShowAddress2(true)}
            >
              + Add Apartment / Suite (optional)
            </button>
          </div>
        )}
      </div>
      <div className="col-12">
        <ValidableControl
          label={(
            <>
              Additional Delivery Details (e.g. gate code, landmark)
              &nbsp;
              <DeliveryInstructionsTooltip />
            </>
          )}
          htmlFor="add-address-delivery-instructions"
          isInvalid={isInvalid('delivery_instructions')}
          errorMessage={getErrorMsg('delivery_instructions')}
        >
          <input
            id="add-address-delivery-instructions"
            aria-label="add address delivery instructions"
            // @ts-expect-error TODO: fix null safety
            ref={deliveryInstructionsRef}
            maxLength={60}
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-xs-4 col-12">
        <ValidableControl
          label="Zipcode *"
          htmlFor="add-address-zip"
          isInvalid={isInvalid('zip')}
          errorMessage={getErrorMsg('zip')}
        >
          <input
            id="add-address-zip"
            aria-label="add address zip"
            // @ts-expect-error TODO: fix null safety
            ref={zipRef}
            type="text"
            pattern="\d*"
            inputMode="numeric"
            maxLength={5}
            value={zip}
            onChange={(e) => setZip(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-xs-8 col-12">
        <ValidableControl
          label="State *"
          htmlFor="add-address-state"
          isInvalid={isInvalid('state')}
          errorMessage={getErrorMsg('state')}
        >
          <div className="c-input-group c-input-group--icon-right">
            <select
              id="add-address-state"
              aria-label="add address state"
              // @ts-expect-error TODO: fix null safety
              ref={stateRef}
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              <option value="" label="Select">&nbsp;</option>
              {Object.entries(State)
                .map(([key, val]) => (
                  <option key={key} value={key}>{val}</option>
                ))}
            </select>
            <button
              type="button"
              aria-label="expand state dropdown"
              className="c-link c-input-icon c-input-icon--right"
            >
              <CaretIcon
                className="c-select__caret-icon c-svg-fill-gray-115-color"
                height="12"
                width="12"
              />
            </button>
          </div>
        </ValidableControl>
      </div>
      <div className="col-xs-6 col-12">
        <ValidableControl
          label="City *"
          htmlFor="add-address-city"
          isInvalid={isInvalid('city')}
          errorMessage={getErrorMsg('city')}
        >
          <input
            id="add-address-city"
            aria-label="add address city"
            // @ts-expect-error TODO: fix null safety
            ref={cityRef}
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </ValidableControl>
      </div>
      <div className="col-xs-6 col-12">
        <ValidableControl
          label="Phone Number *"
          htmlFor="add-address-phone"
          isInvalid={isInvalid('local_phone_number')}
          errorMessage={getErrorMsg('local_phone_number')}
        >
          <input
            id="add-address-phone"
            aria-label="add address phone"
            // @ts-expect-error TODO: fix null safety
            ref={localPhoneNumberRef}
            placeholder="( _ _ _ )  _ _ _ - _ _ _ _"
            type="tel"
            value={phone}
            onInput={(e) => {
              setPhone(maskPhone((e.target as HTMLInputElement).value));
            }}
          />
        </ValidableControl>
      </div>
      <div className="col-12">
        <div className="c-form-group c-signup-checkout__notification-checkboxs">
          <Checkbox
            id="add-account-alerts"
            label="Order & Account Notifications"
            checked={!!smsAccountAlerts}
            onChange={(e) => setSmsAccountAlerts(e.target.checked)}
          />
          <p className="c-profile__notification-checkbox-info">
            Get order updates, tracking details, and other important account alerts.
          </p>

          <Checkbox
            id="add-offers-alerts"
            label="Offers & Updates"
            checked={!!smsOffersAlerts}
            onChange={(e) => setSmsOffersAlerts(e.target.checked)}
          />
          <p className="c-profile__notification-checkbox-info">
            Receive exclusive deals, personalized menu recommendations, and more!
          </p>

          <p className="c-profile__notification-disclaimer">
            By submitting this form, you agree to receive recurring, automated promotional and
            personalized marketing text messages and/or important account updates from Purple Carrot
            at the phone number used when signing up. Message frequency will vary—don&apos;t worry
            we promise not to spam you! Message and data rates will apply. Reply HELP for help or
            STOP to cancel. Consent is not a condition of purchase. View&nbsp;
            <a
              target="_blank"
              rel="noreferrer"
              href="/terms"
            >
              Terms & Conditions
            </a>
            &nbsp;and&nbsp;
            <a
              target="_blank"
              rel="noreferrer"
              href="/privacy"
            >
              Privacy Policy
            </a>
            .
            <br />
            <br />
            * Required Fields
          </p>
        </div>
      </div>
      <div className="col-12">
        <div className="c-form-group">
          <button
            type="button"
            aria-label="submit address"
            disabled={isLoading || submitButtonDisabled}
            className={[
              'c-button',
              'c-button--primary',
              'd-block',
              'd-md-inline-block',
            ].join(' ')}
            onClick={handleSubmit}
          >
            {isFedExValidationEnabled && hasFedExError
              ? 'Verify Address'
              : submitButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddressView;
